import React, { createContext, useContext, useState, useCallback } from "react";
import {
  getAllBoletines,
  getBoletinesByDirector,
  createBoletin,
  updateBoletin,
  getBoletinPDF,
  getObservacion,
  deleteBoletin,

  generarBoletinFinal,
  generarPuestosPeriodoService,
  generarBoletinesFinalesPorSalon,
  getEstudiantesSalonConBoletines,
  getBoletinFinal,
  descargarBoletinFinal, 
  descargarPDFDesdeURL,

  verificarAccesoEstudiante,
  verificarAccesoPeriodo,
  getInhabilitados,
  gestionarVisibilidad,

  validarNotasEstudiante,
  validarNotasSalon
} from "../services/boletinService";
import axios from "axios";

const BoletinContext = createContext();

export const useBoletin = () => useContext(BoletinContext);

export const BoletinProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [estudiantes, setEstudiantes] = useState([]);
  const [progreso, setProgreso] = useState({ actual: 0, total: 0, mensaje: "" });

  const [observacion, setObservacion] = useState(null);
  const [boletines, setBoletines] = useState([]);
  const [boletinesLoaded, setBoletinesLoaded] = useState(false);
  const [boletinesVisibles, setBoletinesVisiblesState] = useState(null);

  // NUEVO: Estados para gestión de visibilidad
  const [inhabilitados, setInhabilitados] = useState([]);
  const [loadingInhabilitados, setLoadingInhabilitados] = useState(false);
  
  // NUEVO: Verificar acceso de un estudiante
  const verificarAcceso = useCallback(async (studentId) => {
    try {
      const response = await verificarAccesoEstudiante(studentId);
      return response.tieneAcceso;
    } catch (error) {
      return false;
    }
  }, []);

  // NUEVO: Verificar acceso a un periodo
  const verificarAccesoAPeriodo = useCallback(async (studentId, periodoId) => {
    try {
      const response = await verificarAccesoPeriodo(studentId, periodoId);
      return response.tieneAcceso;
    } catch (error) {
      return false;
    }
  }, []);

  // NUEVO: Obtener lista de inhabilitados
  const fetchInhabilitados = useCallback(async () => {
    setLoadingInhabilitados(true);
    try {
      const response = await getInhabilitados();
      if (response.success) {
        setInhabilitados(response.data);
        return response.data;
      }
      return [];
    } catch (error) {
      return [];
    } finally {
      setLoadingInhabilitados(false);
    }
  }, []);

  // NUEVO: Gestionar visibilidad (habilitar/inhabilitar)
  const gestionarVisibilidadBoletines = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await gestionarVisibilidad(data);
      
      if (response.success) {
        // Recargar estados relacionados
        await fetchInhabilitados();
      }
      
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // NUEVO: Habilitar estudiante específico
  const habilitarEstudiante = useCallback(async (studentId) => {
    try {
      const response = await gestionarVisibilidad({
        filterType: "estudiante",
        actionType: "habilitar",
        selectedEstudiante: studentId,
        motivo: "Habilitación manual desde lista de inhabilitados",
      });

      if (response.success) {
        await fetchInhabilitados();
      }

      return response;
    } catch (error) {
      throw error;
    }
  }, []);


  const fetchBoletines = async () => {
    try {
      setLoading(true);
      const data = await getAllBoletines();
      setBoletines(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchBoletinesByDirector = useCallback(
    async (force = false) => {
      if (!boletinesLoaded || force) {
        setLoading(true);
        try {
          const data = await getBoletinesByDirector();
          console.log("Boletines por director:", data);
          setBoletines(data || []);
          setBoletinesLoaded(true);
        } catch (error) {
        } finally {
          setLoading(false);
        }
      }
    },
    [boletinesLoaded]
  );

  const fetchObservacion = useCallback(async (studentId, periodoId) => {
    setLoading(true);
    try {
      const data = await getObservacion(studentId, periodoId);
      setObservacion(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  const saveBoletin = async (data) => {
    try {
      setLoading(true);
      const response = await createBoletin(data);
      await fetchBoletinesByDirector(true);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const actualizarBoletin = async (data) => {
    try {
      setLoading(true);
      const response = await updateBoletin(data);
      await fetchBoletinesByDirector(true);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const generarBoletin = async (studentId, periodoId, grado) => {
    try {
      setLoading(true);
      const { pdfUrl } = await getBoletinPDF(studentId, periodoId, grado);

      // Descargar como blob
      const fileResponse = await axios.get(pdfUrl, {
        responseType: "blob",
      });

      const blob = new Blob([fileResponse.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Extraer el nombre del archivo desde la URL completa
      const decodedUrl = decodeURIComponent(pdfUrl);
      const parts = decodedUrl.split("/");
      const rawFilename = parts[parts.length - 1]; // boletin_MARÍA FERNANDA LÓPEZ_1° PERIODO.pdf
      const cleanFilename = rawFilename.endsWith(".pdf")
        ? rawFilename
        : `${rawFilename}.pdf`;

      // Crear y hacer clic en el enlace de descarga
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", cleanFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      await fetchBoletinesByDirector(true);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const eliminarBoletin = async (studentId, periodoId) => {
    setLoading(true);
    try {
      await deleteBoletin(studentId, periodoId);
      await fetchBoletinesByDirector(true);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // CONTEXT BOLETINES FINALES


  // Obtener estudiantes de un salón con información de boletines finales
  const fetchEstudiantesSalon = useCallback(async (salonId) => {
    if (!salonId) return;
    
    setLoading(true);
    try {
      const data = await getEstudiantesSalonConBoletines(salonId);
      setEstudiantes(data || []);
      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Generar boletín final de un estudiante
  const generarBoletinEstudiante = useCallback(async (studentId) => {
    setLoading(true);
    try {
      const response = await generarBoletinFinal(studentId);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Generar boletines finales de todo un salón
  const generarBoletinesSalon = useCallback(async (salonId, estudiantesSeleccionados = null) => {
    setLoading(true);
    setProgreso({ actual: 0, total: 0, mensaje: "Iniciando..." });
    
    try {
      let estudiantesAGenerar = estudiantesSeleccionados;
      
      if (!estudiantesAGenerar) {
        // Si no se especifican estudiantes, obtener todos del salón
        estudiantesAGenerar = await getEstudiantesSalonConBoletines(salonId);
      }
      
      const totalEstudiantes = estudiantesAGenerar.length;
      setProgreso({ actual: 0, total: totalEstudiantes, mensaje: "Generando boletines..." });
      
      const resultados = [];
      
      for (let i = 0; i < estudiantesAGenerar.length; i++) {
        const estudiante = estudiantesAGenerar[i];
        setProgreso({ 
          actual: i + 1, 
          total: totalEstudiantes, 
          mensaje: `Generando boletín de ${estudiante.nombre_completo}...` 
        });
        
        try {
          const resultado = await generarBoletinFinal(estudiante.users_id || estudiante.user_id);
          resultados.push({
            estudiante: estudiante.nombre_completo,
            exito: true,
            data: resultado
          });
        } catch (error) {
          resultados.push({
            estudiante: estudiante.nombre_completo,
            exito: false,
            error: error.message
          });
        }
        
        // Pequeña pausa para no sobrecargar el servidor
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setProgreso({ actual: totalEstudiantes, total: totalEstudiantes, mensaje: "¡Completado!" });
      
      // Refrescar la lista después de generar
      await fetchEstudiantesSalon(salonId);
      
      return resultados;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgreso({ actual: 0, total: 0, mensaje: "" });
      }, 2000);
    }
  }, [fetchEstudiantesSalon]);

  const generarPuestosPeriodo = useCallback(async (periodoId) => {
    setLoading(true);
    setProgreso({ actual: 0, total: 100, mensaje: "Generando tabla de puestos..." });
    
    try {
      const response = await generarPuestosPeriodoService(periodoId);
      
      setProgreso({ actual: 100, total: 100, mensaje: "¡Completado!" });
      
      // Descargar como blob
      const fileResponse = await axios.get(response.pdfUrl, {
        responseType: "blob",
      });

      const blob = new Blob([fileResponse.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Extraer nombre desde la URL
      const decodedUrl = decodeURIComponent(response.pdfUrl);
      const parts = decodedUrl.split("/");
      const rawFilename = parts[parts.length - 1];
      const cleanFilename = rawFilename.endsWith(".pdf")
        ? rawFilename
        : `${rawFilename}.pdf`;

      // Crear enlace de descarga
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", cleanFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: "Tabla de puestos descargada correctamente" };
    } catch (error) {
      console.error("Error generando tabla de puestos:", error);
      throw error;
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgreso({ actual: 0, total: 0, mensaje: "" });
      }, 2000);
    }
  }, []);

  // 🆕 Agregar al BoletinContext
  const validarNotasAntesDeGenerar = useCallback(async (studentId, periodoId) => {
    try {
      const response = await validarNotasEstudiante(studentId, periodoId);
      return response;
    } catch (error) {
      console.error("Error validando notas:", error);
      return { completo: false, mensaje: "Error al validar notas" };
    }
  }, []);

  const validarNotasSalonAntesDeGenerar = useCallback(async (periodoId) => {
    try {
      const response = await validarNotasSalon(periodoId);
      return response;
    } catch (error) {
      console.error("Error validando notas del salón:", error);
      return { completo: false, mensaje: "Error al validar notas del salón" };
    }
  }, []);

  // Obtener boletín final específico
  const obtenerBoletinFinal = useCallback(async (studentId) => {
    try {
      const boletin = await getBoletinFinal(studentId);
      return boletin;
    } catch (error) {
      throw error;
    }
  }, []);


  return (
    <BoletinContext.Provider
      value={{
        loading,
        observacion,
        boletines,
        boletinesVisibles,
        fetchObservacion,
        fetchBoletines,
        fetchBoletinesByDirector,
        saveBoletin,
        actualizarBoletin,
        generarBoletin,
        eliminarBoletin,

        // Boletines Finales
        estudiantes,
        progreso,
        fetchEstudiantesSalon,
        generarBoletinEstudiante,
        generarBoletinesSalon,
        obtenerBoletinFinal,

        generarPuestosPeriodo,

        // Gestión de visibilidad
        inhabilitados,
        loadingInhabilitados,
        verificarAcceso,
        verificarAccesoAPeriodo,
        fetchInhabilitados,
        gestionarVisibilidadBoletines,
        habilitarEstudiante,

        validarNotasAntesDeGenerar,
        validarNotasSalonAntesDeGenerar,
      }}
    >
      {children}
    </BoletinContext.Provider>
  );
};
